import { view } from "@risingstack/react-easy-state";
import { isNumber } from "lodash-es";
import find from "lodash-es/find";
import isEmpty from "lodash-es/isEmpty";
import isNil from "lodash-es/isNil";
import isString from "lodash-es/isString";

import type { SupportedLocaleType } from "../../../common/types/languageTypes";
import type { Product } from "../../../common/types/productTypes";
import { formatEan, formatPrice, translateBaseUnit } from "../../../common/utils";
import useSupportedLocale from "../../../common/utils/useSupportedLocale";

type DataRowProps = {
   name: string;
   value?: string | null;
   showAsEan?: boolean;
};

const DataRow = view(({ name, value, showAsEan = false }: DataRowProps) => {
   if (isNil(value) || isEmpty(value)) {
      return null;
   }

   return (
      <div className="tw-mb-2 tw-flex tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-ink-border-subtle tw-pb-2">
         <div>{name}</div>
         <div className="tw-text-end">{showAsEan ? formatEan(value) : value}</div>
      </div>
   );
});

type ProductInfoProps = {
   product: Product;
};

const formatWeight = (weight: number, locale: SupportedLocaleType) => {
   if (!isNumber(weight) || weight === 0) {
      return null;
   }

   if (weight > 1.0) {
      return `${weight.toLocaleString(locale)} kg`;
   }

   return `${(weight * 1000).toFixed(0)} g`;
};

const ProductInfo = ({ product }: ProductInfoProps) => {
   const locale = useSupportedLocale();
   let brand = product.brand;
   if (isString(brand) && brand.indexOf(",") !== -1) {
      brand = brand.split(",")[1];
   }

   const eanD = find(product.eans, { packaging: "D" })?.code;
   const eanSTK = find(product.eans, { packaging: "STK" })?.code;
   const weight = formatWeight(product.weight, locale);
   const minShelfLife = product.preservable && `${product.preservable} ${product.preservable === "1" ? "dag" : "dager"}`;
   const amount =
      product.unit === "STK" || isNil(product?.conversionFactor) || !isNumber(product.conversionFactor)
         ? 1
         : product.conversionFactor;
   const baseUnit = translateBaseUnit(product.baseUnit, amount);
   const itemsPerPack = `${amount.toFixed(0)} ${baseUnit}`;

   return (
      <>
         <DataRow name="Artikkelnr" value={product.sku} />
         <DataRow name="EPD-nummer" value={product.epdNumber} />
         {weight !== "0" && <DataRow name="Vekt" value={weight} />}
         <DataRow name="Emballasje" value={product.packaging} />
         {isString(brand) && <DataRow name="Merkevare" value={brand} />}
         <DataRow name="EAN, dist.pakning" value={eanD} showAsEan />
         <DataRow name="EAN, forbrukerpakning" value={eanSTK} showAsEan />
         {product.recyclingChargeBaseUnit > 0 && <DataRow name="Pant" value={formatPrice(product.recyclingChargeBaseUnit)} />}
         <DataRow name="Minimum restholdbarhet til detaljist" value={minShelfLife} />
         <DataRow name="Ant. i dist.pakning" value={itemsPerPack} />
         <DataRow name="Oppbevaring" value={product.storage} />
         <DataRow name="Opprinnelsesland" value={product.countryOfOrigin} />
      </>
   );
};

export default view(ProductInfo);
